import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { differenceInDays, intervalToDuration, isBefore } from 'date-fns'
import { DateTime } from 'luxon'

@Component({
  selector: 'ngx-landing-timer',
  templateUrl: './landing-timer.component.html',
  styleUrls: ['./landing-timer.component.scss'],
  imports: [CommonModule]
})
export class LandingTimerComponent implements OnInit, OnDestroy {
  @Input() startDate: string | number
  @Input() endDate: string | number
  @Input() eventTimezone: string
  @Input() isLandingPage = true

  formattedDate: string
  interval: NodeJS.Timeout

  /**
   * Initialize Component and set initial value for formatted date
   */
  ngOnInit(): void {
    this.formatDate()

    this.interval = setInterval(() => {
      this.formatDate()
    }, 1000)
  }

  /**
   * Clear intrval when component is destroyed
   */
  ngOnDestroy(): void {
    clearInterval(this.interval)
  }

  /**
   * Formate Date every second
   * @returns void
   */
  formatDate(): void {
    const now = new Date()
    const start = this._toDate(this.startDate)
    const end = this._toDate(this.endDate)

    if (isBefore(start, now)) {
      if (isBefore(end, now)) {
        this.formattedDate = 'Finished'
        return clearInterval(this.interval)
      }
      this.formattedDate = 'Live'
      return clearInterval(this.interval)
    }

    // Check if event is in the future and not landing page
    if (!isBefore(start, now) && !this.isLandingPage) {
      this.formattedDate = 'Upcoming'
      return clearInterval(this.interval)
    }

    const duration = intervalToDuration({
      start: now,
      end: start
    })

    const days = differenceInDays(start, now)
    if (Object.values(duration).every((v) => !v)) {
      this.formattedDate = 'Live'
      return clearInterval(this.interval)
    }

    this.formattedDate = this._formatTimeRemaining(days, duration)
  }

  /** SQL `yyyy-MM-dd HH:mm:ss` as wall time in `eventTimezone` when set; otherwise `new Date`. */
  private _toDate(value: string | number): Date {
    if (typeof value === 'number') {
      return new Date(value)
    }
    const raw = String(value ?? '').trim()
    const trimmedZone = this.eventTimezone?.trim()
    if (trimmedZone && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)) {
      const dt = DateTime.fromSQL(raw, { zone: trimmedZone })
      if (dt.isValid) {
        return dt.toJSDate()
      }
    }
    return new Date(value)
  }

  /**
   * Format time remaining
   * @param days - number
   * @param duration - Duration
   * @returns string
   */
  private _formatTimeRemaining(days: number, duration: Duration): string {
    const formatUnit = (value: number | undefined, unit: string) => (value ? `${value} ${value === 1 ? unit : unit + 's'}` : '')

    if (days) {
      const hoursStr = formatUnit(duration.hours, 'hour')
      return `${formatUnit(days, 'day')}${hoursStr ? ` & ${hoursStr}` : ''} to start`
    } else if (duration.hours) {
      const minutesStr = formatUnit(duration.minutes, 'minute')
      return `${formatUnit(duration.hours, 'hour')}${minutesStr ? ` & ${minutesStr}` : ''} to start`
    } else if (duration.minutes) {
      const secondsStr = formatUnit(duration.seconds, 'second')
      return `${formatUnit(duration.minutes, 'minute')}${secondsStr ? ` & ${secondsStr}` : ''} to start`
    } else {
      return `${formatUnit(duration.seconds, 'second')} to start`
    }
  }
}
